import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {GatewayGetSchema} from '../../../../../../api/DBSchema/Gateway';
import PlanEditorInnerContainer from '../../../../../../components/Editor/PlanEditorInnerContainer';
import Separator from '../../../../../../components/Simple/Sepperator';
import SMCLabel from '../../../../../../components/Simple/SMCLabel';
import {useToggle} from '../../../../../../utils/hooks';
import EmptyRow from '../../../../../../components/Simple/EmptyRow';
import EditorLayout from '../../../../../../components/Layouts/EditorLayout';
import SMCLink from '../../../../../../components/Simple/SMCLink';
import AccessPolicyTableLegacy from './AccessPolicyTableLegacy';
import AccessPolicyTableNextGen from './AccessPolicyTableNextGen';
import SMCCollapsibleNote from '../../../../../../components/Simple/SMCCollapsibleNote';
import {SMCToggleSwitch} from "../../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.accessPolicy;
const pageId = pageSchema.pageId;

function BaseAccessPolicy({
                              baseObjectName,
                              pageData,
                              setObjectData,
                              pageDisabled,
                              objectType,
                              appiData,
                              uoTableData,
                              zonesData,
                              setUoTableDataValue,
                              setAppiDataValue
                          }) {
    const {t} = useTranslation();
    const [expandedLegacy, toggleExpandedLegacy] = useToggle(true);
    const [expandedNextGen, toggleExpandedNextGen] = useToggle(true);

    const managedInSmp = pageData[pageSchema.manageInSmp];
    const ruleBaseType = pageData[pageSchema.ruleBaseType];
    const legacyRules = pageData[pageSchema.legacyRules];
    const nextGenRules = pageData[pageSchema.nextGenRules];

    const isLegacy = useMemo(() => ruleBaseType === pageSchema.ruleBaseTypeValues.LEGACY, [ruleBaseType]);

    const preOutgoingRules = isLegacy ? legacyRules[pageSchema.cpAccessRulesSchema.preOutgoingRules] : nextGenRules[pageSchema.cpAccessRulesSchema.preOutgoingRules];
    const postOutgoingRules = isLegacy ? legacyRules[pageSchema.cpAccessRulesSchema.postOutgoingRules] : nextGenRules[pageSchema.cpAccessRulesSchema.postOutgoingRules];
    const preIncomingRules = isLegacy ? legacyRules[pageSchema.cpAccessRulesSchema.preIncomingRules] : nextGenRules[pageSchema.cpAccessRulesSchema.preIncomingRules];
    const postIncomingRules = isLegacy ? legacyRules[pageSchema.cpAccessRulesSchema.postIncomingRules] : nextGenRules[pageSchema.cpAccessRulesSchema.postIncomingRules];

    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    const setRules = useCallback((param, newValue) => {
        if (isLegacy) {
            setObjectData([pageId, pageSchema.legacyRules, param], newValue);
        } else {
            setObjectData([pageId, pageSchema.nextGenRules, param], newValue);
        }
    }, [setObjectData, isLegacy]);

    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={managedInSmp}
                onChange={checked => setPageValue(pageSchema.manageInSmp, checked)}
                label={t('SMP.PLAN.MANAGE_IN_SMP')}
            />

            <PlanEditorInnerContainer managedInSmp={managedInSmp}>
                {(ruleBaseType === pageSchema.ruleBaseTypeValues.LEGACY) ?
                    (
                        <SMCCollapsibleNote
                            variant={'note'}
                            disabled={pageDisabled}
                            toggleBool={expandedLegacy}
                            toggleFunc={toggleExpandedLegacy}
                            style={{textAlign: 'center', background: '#b1dbff'}}
                        >

                            <b> {t('SMP.PLAN.SEC_SOFT_BLADES.NEXT_GEN')}</b>&nbsp;{t('SMP.PLAN.SEC_SOFT_BLADES.LEGACY.AVAILABLE')}
                            <b>{t('SMP.PLAN.SEC_SOFT_BLADES.R80.20.30')}</b> &nbsp;{t('SMP.PLAN.SEC_SOFT_BLADES.HIGHER')}.<br/>
                            <SMCLink inline bold underline style={{color: 'black'}}
                                     onClick={() => setPageValue(pageSchema.ruleBaseType, pageSchema.ruleBaseTypeValues.NEXT_GEN)}>{t('SMP.PLAN.SEC_SOFT_BLADES.CLICK')}</SMCLink> {t('SMP.PLAN.SEC_SOFT_BLADES.TO_SWITCH')}
                            <b>{t('SMP.PLAN.SEC_SOFT_BLADES.NEXT_GEN')}</b>.
                            {t('SMP.PLAN.SEC_SOFT_BLADES.INFORMATION')}&nbsp;
                            <a href="https://supportcenter.checkpoint.com/supportcenter/portal?eventSubmit_doGoviewsolutiondetails=&solutionid=sk118035"
                               rel="noopener noreferrer" target="_blank" underline="true" style={{color: 'black'}}>sk118035</a>.<br/>
                        </SMCCollapsibleNote>
                    ) :
                    (

                        <SMCCollapsibleNote
                            disabled={pageDisabled}
                            toggleBool={expandedNextGen}
                            toggleFunc={toggleExpandedNextGen}
                            style={{background: '#f8f8f8'}}
                        >
                            <>
                                <b>{t('SMP.PLAN.SEC_SOFT_BLADES.NEXT_GEN')}</b>{t('SMP.PLAN.SEC_SOFT_BLADES.RUNNING')}<b>&nbsp;{t('SMP.PLAN.SEC_SOFT_BLADES.R80.20.30')}</b>&nbsp;{t('SMP.PLAN.SEC_SOFT_BLADES.HIGHER')}.<br/>
                                &nbsp;&emsp;&ensp;{t('SMP.PLAN.SEC_SOFT_BLADES.OLDER_VERSIONS')} <SMCLink bold underline
                                                                                                          style={{color: 'black'}}
                                                                                                          inline
                                                                                                          onClick={() => setPageValue(pageSchema.ruleBaseType, pageSchema.ruleBaseTypeValues.LEGACY)}> {t('SMP.PLAN.SEC_SOFT_BLADES.LEGACY')}</SMCLink> {t('SMP.PLAN.SEC_SOFT_BLADES.INFORMATION')}&nbsp;
                                <a href="https://supportcenter.checkpoint.com/supportcenter/portal?eventSubmit_doGoviewsolutiondetails=&solutionid=sk118035"
                                   rel="noopener noreferrer" target="_blank" underline="true"
                                   style={{color: 'black'}}>sk118035</a>.&nbsp; </>

                        </SMCCollapsibleNote>
                    )
                }
                <EmptyRow/>
                <EmptyRow/>
                                          {/* Pre local rules */}
                    {isLegacy ? (
                        <>
                            <EmptyRow/>
                            <SMCLabel bold fontSize="18px">{t('SMP.PLAN.SEC_SOFT_BLADES.LINE5')}</SMCLabel>
                            <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.PRE_LOCAL_RULES')}</SMCLabel>
                            <AccessPolicyTableLegacy
                                rules={preOutgoingRules}
                                disabled={pageDisabled || !managedInSmp}
                                setRules={rules => setRules(pageSchema.cpAccessRulesSchema.preOutgoingRules, rules)}
                                baseObjectName={baseObjectName}
                                objectType={objectType}
                                incomingOrOutgoing={'outgoing'}
                                legacyOrNextGen={'legacy'}
                                appiData={appiData}
                                uoTableData={uoTableData}
                                zonesData={zonesData}
                            />
                            <EmptyRow/>
                            <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.POST_LOCAL_RULES')}</SMCLabel>
                            <AccessPolicyTableLegacy
                                rules={postOutgoingRules}
                                disabled={pageDisabled || !managedInSmp}
                                setRules={rules => setRules(pageSchema.cpAccessRulesSchema.postOutgoingRules, rules)}
                                baseObjectName={baseObjectName}
                                objectType={objectType}
                                incomingOrOutgoing={'outgoing'}
                                legacyOrNextGen={'legacy'}
                                appiData={appiData}
                                uoTableData={uoTableData}
                                zonesData={zonesData}
                            />
                            <EmptyRow/>
                            <Separator/>
                            <EmptyRow/>
                            <SMCLabel bold fontSize="18px">{t('SMP.PLAN.SEC_SOFT_BLADES.LINE6')}</SMCLabel>
                            <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.PRE_LOCAL_RULES')}</SMCLabel>
                            <AccessPolicyTableLegacy
                                rules={preIncomingRules}
                                disabled={pageDisabled || !managedInSmp}
                                setRules={rules => setRules(pageSchema.cpAccessRulesSchema.preIncomingRules, rules)}
                                baseObjectName={baseObjectName}
                                objectType={objectType}
                                incomingOrOutgoing={'incoming'}
                                legacyOrNextGen={'legacy'}
                                uoTableData={uoTableData}
                                zonesData={zonesData}
                            />
                            <EmptyRow/>
                            <SMCLabel>{t('SMP.PLAN.SEC_SOFT_BLADES.POST_LOCAL_RULES')}</SMCLabel>
                            <AccessPolicyTableLegacy
                                rules={postIncomingRules}
                                disabled={pageDisabled || !managedInSmp}
                                setRules={rules => setRules(pageSchema.cpAccessRulesSchema.postIncomingRules, rules)}
                                baseObjectName={baseObjectName}
                                objectType={objectType}
                                incomingOrOutgoing={'incoming'}
                                legacyOrNextGen={'legacy'}
                                uoTableData={uoTableData}
                                zonesData={zonesData}
                            />
                        </>
                    ) : (
                        <>
                            <SMCLabel bold fontSize="18px">{t('SMP.PLAN.SEC_SOFT_BLADES.LINE5')}</SMCLabel>
                            <EmptyRow/>
                            <AccessPolicyTableNextGen
                                preRules={preOutgoingRules}
                                postRules={postOutgoingRules}
                                disabled={pageDisabled || !managedInSmp}
                                setPreRules={rules => setRules(pageSchema.cpAccessRulesSchema.preOutgoingRules, rules)}
                                setPostRules={rules => setRules(pageSchema.cpAccessRulesSchema.postOutgoingRules, rules)}
                                baseObjectName={baseObjectName}
                                objectType={objectType}
                                incomingOrOutgoing='outgoing'
                                legacyOrNextGen={isLegacy ? 'legacy' : 'nextGen'}
                                appiData={appiData}
                                uoTableData={uoTableData}
                                zonesData={zonesData}
                                setUoTableDataValue={setUoTableDataValue}
                                setAppiDataValue={setAppiDataValue}
                            />
                            <EmptyRow/>
                            <EmptyRow/>
                            <SMCLabel bold fontSize="18px">{t('SMP.PLAN.SEC_SOFT_BLADES.LINE6')}</SMCLabel>
                            <EmptyRow/>
                            <AccessPolicyTableNextGen
                                preRules={preIncomingRules}
                                postRules={postIncomingRules}
                                disabled={pageDisabled || !managedInSmp}
                                setPreRules={rules => setRules(pageSchema.cpAccessRulesSchema.preIncomingRules, rules)}
                                setPostRules={rules => setRules(pageSchema.cpAccessRulesSchema.postIncomingRules, rules)}
                                baseObjectName={baseObjectName}
                                objectType={objectType}
                                incomingOrOutgoing='incoming'
                                legacyOrNextGen={isLegacy ? 'legacy' : 'nextGen'}
                                appiData={appiData}
                                uoTableData={uoTableData}
                                zonesData={zonesData}
                                setUoTableDataValue={setUoTableDataValue}
                                setAppiDataValue={setAppiDataValue}
                            />
                        </>
                    )
                    }
                <EmptyRow/>
            </PlanEditorInnerContainer>
        </EditorLayout>
    );
}


export default BaseAccessPolicy;
